import { create } from "zustand";
import type {
  CalculatedReportSummary,
  DailyRevenueRecord,
  ExpenseRecord,
  ImportedWorkbook,
  PropertyInformation,
  ReportingPeriod,
  SourceReportedSummary,
  TenantAccountRecord,
  ValidationIssue,
  WorkbookLayout,
  WorkbookMetadata,
} from "../types/report";
import {
  calculateDailyRecord,
  calculateSummary,
  calculateTenantRecord,
} from "../features/calculation/calculations";
import { createId } from "../utils/ids";
import { format } from "date-fns";

type Step = "upload" | "review" | "settings" | "preview" | "complete";

interface ReportBuilderState {
  sourceFile: File | null;
  workbookMetadata: WorkbookMetadata | null;
  detectedLayout: WorkbookLayout | null;
  property: PropertyInformation;
  reportingPeriod: ReportingPeriod | null;
  dailyRevenue: DailyRevenueRecord[];
  tenantAccounts: TenantAccountRecord[];
  expenses: ExpenseRecord[];
  sourceReportedSummary: SourceReportedSummary;
  calculatedSummary: CalculatedReportSummary | null;
  validationIssues: ValidationIssue[];
  notesToLandlord: string;
  currentStep: Step;
  isParsing: boolean;
  parseProgress: string;
  isGeneratingPdf: boolean;
  fatalError: string | null;
  importWorkbookFile: (file: File) => Promise<void>;
  applyImportedWorkbook: (result: ImportedWorkbook) => void;
  updateDailyRevenueRecord: (
    id: string,
    changes: Partial<DailyRevenueRecord>,
  ) => void;
  updateTenantRecord: (
    id: string,
    changes: Partial<TenantAccountRecord>,
  ) => void;
  updateExpenseRecord: (id: string, changes: Partial<ExpenseRecord>) => void;
  addDailyRevenueRecord: () => void;
  addTenantRecord: () => void;
  addExpenseRecord: () => void;
  deleteRecord: (section: "daily" | "tenant" | "expense", id: string) => void;
  resolveIssue: (id: string) => void;
  updateProperty: (changes: Partial<PropertyInformation>) => void;
  setReportingPeriod: (changes: Partial<ReportingPeriod>) => void;
  setNotes: (notes: string) => void;
  setStep: (step: Step) => void;
  setGeneratingPdf: (value: boolean) => void;
  recalculate: () => void;
  resetReport: () => void;
}

const defaultProperty: PropertyInformation = {
  preparedBy: "",
  reportTitle: "Monthly Parking Operations Report",
  propertyAddress: "",
  logoDataUrl: null,
  accentColor: "#123B5D",
};

function readPreferences(): Partial<PropertyInformation> {
  try {
    const raw = localStorage.getItem("parkledger.preferences");
    return raw ? (JSON.parse(raw) as Partial<PropertyInformation>) : {};
  } catch {
    return {};
  }
}

function savePreferences(property: PropertyInformation): void {
  const preferences = {
    preparedBy: property.preparedBy,
    reportTitle: property.reportTitle,
    propertyAddress: property.propertyAddress,
    accentColor: property.accentColor,
    logoDataUrl:
      property.logoDataUrl && property.logoDataUrl.length < 750_000
        ? property.logoDataUrl
        : null,
  };
  try {
    localStorage.setItem("parkledger.preferences", JSON.stringify(preferences));
  } catch {
    // Local preferences are optional. Report data remains in memory.
  }
}

function summaryFor(state: ReportBuilderState): CalculatedReportSummary {
  return calculateSummary(
    state.dailyRevenue,
    state.tenantAccounts,
    state.expenses,
    state.reportingPeriod,
  );
}

function refreshDuplicateDateIssues(
  issues: ValidationIssue[],
  records: DailyRevenueRecord[],
): ValidationIssue[] {
  const next = issues.filter((issue) => issue.type !== "duplicate_date");
  const grouped = new Map<string, DailyRevenueRecord[]>();
  records.forEach((record) =>
    grouped.set(record.date, [...(grouped.get(record.date) ?? []), record]),
  );
  grouped.forEach((matches, date) => {
    if (matches.length < 2) return;
    matches.forEach((record) =>
      next.push({
        id: createId("issue"),
        severity: "error",
        type: "duplicate_date",
        message: `${date} appears more than once. Delete or correct the duplicate before generating the report.`,
        section: "daily_revenue",
        sourceRow: record.sourceRow,
        recordId: record.id,
        resolved: false,
      }),
    );
  });
  return next;
}

export const useReportBuilderStore = create<ReportBuilderState>((set, get) => ({
  sourceFile: null,
  workbookMetadata: null,
  detectedLayout: null,
  property: { ...defaultProperty, ...readPreferences() },
  reportingPeriod: null,
  dailyRevenue: [],
  tenantAccounts: [],
  expenses: [],
  sourceReportedSummary: {},
  calculatedSummary: null,
  validationIssues: [],
  notesToLandlord: "",
  currentStep: "upload",
  isParsing: false,
  parseProgress: "",
  isGeneratingPdf: false,
  fatalError: null,

  importWorkbookFile: async (file) => {
    set({
      sourceFile: file,
      isParsing: true,
      parseProgress: "Reading workbook",
      fatalError: null,
    });
    try {
      set({ parseProgress: "Detecting report sections" });
      const { importWorkbook } =
        await import("../features/workbook-import/workbookImporter");
      const result = await importWorkbook(file);
      set({ parseProgress: "Validating totals" });
      get().applyImportedWorkbook(result);
    } catch (error) {
      set({
        fatalError:
          error instanceof Error
            ? error.message
            : "The workbook could not be imported.",
        isParsing: false,
        parseProgress: "",
      });
    }
  },

  applyImportedWorkbook: (result) => {
    const next = {
      workbookMetadata: result.metadata,
      detectedLayout: result.layout,
      reportingPeriod: result.reportingPeriod,
      dailyRevenue: result.dailyRevenue,
      tenantAccounts: result.tenantAccounts,
      expenses: result.expenses,
      sourceReportedSummary: result.sourceReportedSummary,
      validationIssues: result.validationIssues,
      currentStep: "review" as const,
      isParsing: false,
      parseProgress: "",
      fatalError: null,
    };
    set(next);
    set({ calculatedSummary: summaryFor(get()) });
  },

  updateDailyRevenueRecord: (id, changes) => {
    set((state) => ({
      ...(() => {
        const dailyRevenue = state.dailyRevenue.map((record) =>
          record.id === id
            ? calculateDailyRecord({ ...record, ...changes })
            : record,
        );
        return {
          dailyRevenue,
          validationIssues: refreshDuplicateDateIssues(
            state.validationIssues,
            dailyRevenue,
          ),
        };
      })(),
    }));
    get().recalculate();
  },

  updateTenantRecord: (id, changes) => {
    set((state) => ({
      tenantAccounts: state.tenantAccounts.map((record) =>
        record.id === id
          ? calculateTenantRecord({
              ...record,
              ...changes,
              sourceReceived: undefined,
            })
          : record,
      ),
    }));
    get().recalculate();
  },

  updateExpenseRecord: (id, changes) => {
    set((state) => ({
      expenses: state.expenses.map((record) =>
        record.id === id ? { ...record, ...changes } : record,
      ),
    }));
    get().recalculate();
  },

  addDailyRevenueRecord: () => {
    const date =
      get().reportingPeriod?.endDate ?? format(new Date(), "yyyy-MM-dd");
    const record: DailyRevenueRecord = calculateDailyRecord({
      id: createId("daily"),
      sourceRow: 0,
      date,
      dayOfWeek: format(new Date(`${date}T12:00:00`), "EEEE"),
      income: Object.fromEntries(
        Object.keys(
          get().dailyRevenue[0]?.income ?? {
            Cash: 0,
            "Credit Card": 0,
            Zelle: 0,
            "Venmo App": 0,
          },
        ).map((label) => [label, 0]),
      ),
      startingTicket: 0,
      endingTicket: 0,
      totalTicket: 0,
      dailyTotal: 0,
    });
    set((state) => ({ dailyRevenue: [...state.dailyRevenue, record] }));
    get().recalculate();
  },

  addTenantRecord: () => {
    const record: TenantAccountRecord = {
      id: createId("tenant"),
      sourceRow: 0,
      companyName: "New tenant",
      normalizedCompanyName: "new tenant",
      floor: null,
      suiteNumber: null,
      accountType: "tenant",
      sunriseCount: 0,
      smmdCount: 0,
      amountDue: 0,
      amountReceived: 0,
      checkAmount: 0,
      creditCardAmount: 0,
      zelleAmount: 0,
      wireAchAmount: 0,
      cashAmount: 0,
      kioskAmount: 0,
      validationAmount: 0,
      paymentDate: null,
      paymentMethod: null,
      checkNumber: null,
      balance: 0,
      status: "no_charge",
      notes: null,
      additionalFields: {},
    };
    set((state) => ({ tenantAccounts: [...state.tenantAccounts, record] }));
    get().recalculate();
  },

  addExpenseRecord: () => {
    const record: ExpenseRecord = {
      id: createId("expense"),
      sourceRow: 0,
      originalLabel: "New expense",
      displayLabel: "New expense",
      category: "other",
      amount: 0,
      notes: null,
    };
    set((state) => ({ expenses: [...state.expenses, record] }));
    get().recalculate();
  },

  deleteRecord: (section, id) => {
    set((state) => {
      const dailyRevenue =
        section === "daily"
          ? state.dailyRevenue.filter((record) => record.id !== id)
          : state.dailyRevenue;
      const remainingIssues = state.validationIssues.filter(
        (issue) => issue.recordId !== id,
      );
      return {
        dailyRevenue,
        tenantAccounts:
          section === "tenant"
            ? state.tenantAccounts.filter((record) => record.id !== id)
            : state.tenantAccounts,
        expenses:
          section === "expense"
            ? state.expenses.filter((record) => record.id !== id)
            : state.expenses,
        validationIssues:
          section === "daily"
            ? refreshDuplicateDateIssues(remainingIssues, dailyRevenue)
            : remainingIssues,
      };
    });
    get().recalculate();
  },

  resolveIssue: (id) =>
    set((state) => ({
      validationIssues: state.validationIssues.map((issue) =>
        issue.id === id ? { ...issue, resolved: true } : issue,
      ),
    })),

  updateProperty: (changes) => {
    set((state) => ({ property: { ...state.property, ...changes } }));
    savePreferences(get().property);
  },

  setReportingPeriod: (changes) => {
    set((state) => ({
      reportingPeriod: state.reportingPeriod
        ? { ...state.reportingPeriod, ...changes }
        : {
            startDate: changes.startDate ?? "",
            endDate: changes.endDate ?? "",
            monthLabel: changes.monthLabel ?? "",
          },
    }));
    get().recalculate();
  },

  setNotes: (notesToLandlord) => set({ notesToLandlord }),
  setStep: (currentStep) => set({ currentStep }),
  setGeneratingPdf: (isGeneratingPdf) => set({ isGeneratingPdf }),

  recalculate: () => set({ calculatedSummary: summaryFor(get()) }),

  resetReport: () =>
    set({
      sourceFile: null,
      workbookMetadata: null,
      detectedLayout: null,
      property: { ...defaultProperty, ...readPreferences() },
      reportingPeriod: null,
      dailyRevenue: [],
      tenantAccounts: [],
      expenses: [],
      sourceReportedSummary: {},
      calculatedSummary: null,
      validationIssues: [],
      notesToLandlord: "",
      currentStep: "upload",
      isParsing: false,
      parseProgress: "",
      isGeneratingPdf: false,
      fatalError: null,
    }),
}));
